import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { Badge } from '../../components/UI/Badge';
import { Pagination } from '../../components/UI/Pagination';
import { Spinner } from '../../components/UI/Spinner';
import { EmptyState } from '../../components/UI/EmptyState';
import {
  CreditCard,
  Search,
  Zap,
} from 'lucide-react';

export const AdminSubscriptions = () => {
  const [users, setUsers] = useState([]);
  const [totalSubscribed, setTotalSubscribed] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const { success, error: toastError } = useToast();

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.subscriptions({ search, page });
      if (res && res.data) {
        setUsers(res.data.data || []);
        setPagination({
          currentPage: res.data.current_page,
          lastPage: res.data.last_page,
          total: res.data.total,
          from: res.data.from,
          to: res.data.to,
        });
        if (res.totalSubscribed !== undefined) {
          setTotalSubscribed(res.totalSubscribed);
        }
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar suscripciones.');
    } finally {
      setLoading(false);
    }
  }, [search, page, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSubscriptions();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchSubscriptions]);

  const handleToggle = async (user) => {
    setTogglingId(user.id);
    try {
      const res = await adminService.toggleSubscription(user.id);
      success(res.message || 'Estado de suscripción actualizado.');
      fetchSubscriptions();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al modificar suscripción.');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
            Gestión de Suscripciones & Ingresos Recurrentes
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Supervisa los usuarios con membresía Pro y activa o revoca accesos manualmente
          </p>
        </div>

        <div className="px-4 py-2 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 rounded-2xl flex items-center gap-3">
          <Zap className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <div>
            <p className="text-[10px] uppercase font-bold text-emerald-700 dark:text-emerald-300">
              Total Suscripciones Activas
            </p>
            <p className="text-lg font-black text-emerald-950 dark:text-emerald-100">
              {totalSubscribed} Clientes PRO (${(totalSubscribed * 9.99).toFixed(2)}/mes)
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="w-full sm:w-80">
            <Input
              placeholder="Buscar por usuario o ID PayPal..."
              icon={Search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="py-1.5"
            />
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {loading ? (
            <Spinner label="Cargando suscriptores..." className="py-12" />
          ) : users.length === 0 ? (
            <EmptyState
              icon={CreditCard}
              title="No hay suscriptores encontrados"
              description="No hay usuarios suscritos bajo los criterios de búsqueda actuales."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4">Usuario</th>
                    <th className="py-3 px-4">Plan Actual</th>
                    <th className="py-3 px-4">ID Suscripción PayPal</th>
                    <th className="py-3 px-4 text-center">Estado Membresía</th>
                    <th className="py-3 px-4 text-right">Acción Manual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-900 dark:text-slate-100">{u.name}</div>
                        <div className="text-[11px] text-slate-400">{u.email}</div>
                      </td>

                      <td className="py-3.5 px-4">
                        <Badge variant="primary" size="sm">Plan Profesional ($9.99)</Badge>
                      </td>

                      <td className="py-3.5 px-4 font-mono text-slate-600 dark:text-slate-300">
                        {u.paypal_subscription_id || 'Membresía Manual / Sin ID'}
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        {u.is_subscribed ? (
                          <Badge variant="success" size="sm" dot>Activa</Badge>
                        ) : (
                          <Badge variant="danger" size="sm">Cancelada / Inactiva</Badge>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <Button
                          variant={u.is_subscribed ? 'secondary' : 'success'}
                          size="sm"
                          isLoading={togglingId === u.id}
                          onClick={() => handleToggle(u)}
                        >
                          {u.is_subscribed ? 'Revocar Pro' : 'Activar Pro'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pagination.currentPage}
            lastPage={pagination.lastPage}
            total={pagination.total}
            from={pagination.from}
            to={pagination.to}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>
    </div>
  );
};
